import { useCallback, useEffect, useRef, useState } from "react";

// Posição arrastável de um botão flutuante, lembrada no navegador.
// - Arrastar move o botão; um toque/clique sem arrastar continua sendo clique.
// - A posição fica sempre dentro da tela (inclusive ao girar o tablet).
// - Sem posição salva, o botão usa o canto padrão definido pelo CSS.
export const DRAG_THRESHOLD = 6;
const MARGIN = 8;

export function clampPosition(pos, size, viewport) {
  if (!pos) return null;
  const maxX = Math.max(MARGIN, viewport.width - size - MARGIN);
  const maxY = Math.max(MARGIN, viewport.height - size - MARGIN);
  return { x: Math.min(Math.max(pos.x, MARGIN), maxX), y: Math.min(Math.max(pos.y, MARGIN), maxY) };
}

// Onde abrir o painel: alinhado ao botão, acima se o botão estiver na metade de
// baixo da tela, abaixo se estiver na metade de cima, sem sair da tela.
export function panelPlacement(pos, size, panel, viewport) {
  const width = Math.min(panel.width, viewport.width - 2 * MARGIN);
  const height = Math.min(panel.height, viewport.height - size - 3 * MARGIN);
  const left = Math.min(Math.max(pos.x + size - width, MARGIN), viewport.width - width - MARGIN);
  const above = pos.y + size / 2 > viewport.height / 2;
  const rawTop = above ? pos.y - height - 12 : pos.y + size + 12;
  const top = Math.min(Math.max(rawTop, MARGIN), viewport.height - height - MARGIN);
  return { left, top, width, height };
}

function viewport() {
  return { width: window.innerWidth, height: window.innerHeight };
}

function load(key) {
  try {
    const saved = JSON.parse(window.localStorage.getItem(key) || "null");
    return saved && Number.isFinite(saved.x) && Number.isFinite(saved.y) ? saved : null;
  } catch {
    return null;
  }
}

function save(key, pos) {
  try { window.localStorage.setItem(key, JSON.stringify(pos)); } catch { /* armazenamento indisponível */ }
}

export function useFloatingPosition(key, size) {
  const [pos, setPos] = useState(() => clampPosition(load(key), size, viewport()));
  const drag = useRef(null);
  const moved = useRef(false);

  useEffect(() => {
    const onResize = () => setPos((current) => clampPosition(current, size, viewport()));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [size]);

  const onPointerDown = useCallback((event) => {
    if (event.button !== undefined && event.button !== 0) return;
    const rect = event.currentTarget.getBoundingClientRect();
    drag.current = { startX: event.clientX, startY: event.clientY, originX: rect.left, originY: rect.top };
    moved.current = false;
    try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch { /* sem captura */ }
  }, []);

  const onPointerMove = useCallback((event) => {
    const state = drag.current;
    if (!state) return;
    const dx = event.clientX - state.startX;
    const dy = event.clientY - state.startY;
    if (!moved.current && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    moved.current = true;
    setPos(clampPosition({ x: state.originX + dx, y: state.originY + dy }, size, viewport()));
  }, [size]);

  const onPointerUp = useCallback(() => {
    if (!drag.current) return;
    drag.current = null;
    if (moved.current) setPos((current) => { if (current) save(key, current); return current; });
  }, [key]);

  // O clique que encerra um arraste não deve abrir/fechar o painel.
  const consumeDrag = useCallback(() => {
    const wasDrag = moved.current;
    moved.current = false;
    return wasDrag;
  }, []);

  return {
    pos,
    consumeDrag,
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp },
  };
}
